<script>
import Card from '../../../Components/Card.vue';
import flatpickr from 'flatpickr';
import moment from 'moment-timezone';
import Loading from '../../../Components/Loading.vue';
import { genericError } from '../../../Modules/Alerts.js';
import Alert from '../../../Components/Alert.vue';

export default {
  components: { Alert, Loading, Card },

  props: {
    masterConfig: Object,
    kioskKey: String,
    services: Array,
    selected: {
      type: String,
      default: '',
    },
    initialAvailableDates: {
      type: Object,
      default: null,
    },
    firstDayAvailable: {
      type: String,
      default: '',
    },
    tireConnectData: {
      type: Object,
      default: null,
    },
  },

  emits: ['ready', 'update', 'waiter', 'preference'],

  data() {
    return {
      targetMonth: null,
      availabilityAbortController: null,
      availableDatesCache: {},
      initialized: false,
      selectedDate: null,
      calendar: null,
      loading: false,
      appointment: null,
      settings: [],
      slots: {},
      availableDates: this.initialAvailableDates ?? {},
      appointmentSlot: null,
      availableSlotsLoading: false,
      pendingSlotFetch: false,
      startDate: null,
      lastDate: null,
      hasFetched: false,
      initialCalendarDateApplied: false,
    };
  },

  computed: {
    hasSlots() {
      return Object.keys(this.slots).length > 0;
    },

    validTireConnectData() {
      const d = this.tireConnectData;
      if (!d || typeof d !== 'object') return null;
      if (typeof d.key === 'string' && d.key.startsWith('__')) return null;
      return d;
    },
  },

  watch: {
    services: {
      handler() {
        this.availableDatesCache = {};
      },
      deep: true,
    },
    selectedDate(newValue, oldValue) {
      if (!this.initialized) return;
      this.getAvailability();
    },
    firstDayAvailable() {
      if (!this.initialCalendarDateApplied) {
        this.recomputeCalendarFromEarliestDate();
      } else {
        this.refreshCalendarDates();
      }
    },
    initialAvailableDates(newValue) {
      this.availableDates = newValue ?? {};
      if (!this.initialCalendarDateApplied && this.initialized) {
        this.applyInitialCalendarDate(this.calendar);
      } else {
        this.refreshCalendarDates();
      }
    },
    tireConnectData: {
      handler() {
        this.availableDatesCache = {};

        this.recomputeCalendarFromEarliestDate();
      },
      deep: true,
    },
  },

  mounted() {
    this.getSettings();
    if (this.selected !== null) {
      this.appointmentSlot = this.selected;
      this.$emit('ready', true);
    }
  },

  methods: {
    recomputeCalendarFromEarliestDate() {
      if (!this.calendar) return;

      const nextStartDate = this.startingDate();
      if (!nextStartDate) return;

      this.appointmentSlot = null;
      this.$emit('ready', false);
      this.$emit('update', null);

      this.calendar.setDate(nextStartDate, true);
      this.calendar.jumpToDate(nextStartDate);

      const visibleDates = this.getFirstAndLastVisibleDates(this.calendar);
      if (visibleDates) {
        this.getAvailableDates(visibleDates);
      }
    },

    updateSlot(slot, availability) {
      this.appointmentSlot = slot;
      this.$emit('ready', true);
      this.$emit('update', this.appointmentSlot);
      this.$emit('preference', 'd');

      if (availability === 'Dropoff Only Available') {
        this.$emit('waiter', false);
      } else {
        this.$emit('waiter', true);
      }
    },

    getSettings() {
      axios.post(route('scheduler.settings', { key: this.kioskKey })).then((response) => {
        this.settings = response.data.data;
        this.setupDatePicker();
      });
    },

    startingDate() {
      if (this.firstDayAvailable) {
        return moment(this.firstDayAvailable).format('YYYY-MM-DD');
      }
      const addHours = this.settings.slot_settings.next_available_appt || 0;
      const now = moment().add(addHours, 'hours');
      return now.format('YYYY-MM-DD');
    },

    setupDatePicker() {
      const vm = this;

      this.calendar = flatpickr('#scheduler_calendar', {
        inline: true,
        disable: [
          function (date) {
            const days = !vm.settings['days_of_week'].includes(date.getDay().toString());
            let startingBefore = false;
            if (vm.startingDate() !== null) {
              const startDate = moment(vm.startingDate());
              if (moment(date).isBefore(startDate, 'day')) {
                startingBefore = true;
              }
            }
            return days || startingBefore;
          },
        ],
        dateFormat: 'Y-m-d',
        defaultDate: vm.startingDate(),
        minDate: 'today',
        onDayCreate: function (selectedDates, dateStr, instance, dayElem) {
          const dateString = vm.formatLocal(dayElem.dateObj);
          const rangeStart = vm.startDate ? vm.formatLocal(new Date(vm.startDate)) : null;
          const rangeEnd = vm.lastDate ? vm.formatLocal(new Date(vm.lastDate)) : null;
          const withinRange =
            rangeStart && rangeEnd && dateString >= rangeStart && dateString <= rangeEnd;

          dayElem.classList.remove('no-slots');
          if (withinRange && !vm.availableDates[dateString]) {
            dayElem.classList.add('no-slots');
          }
        },
        onChange: function (selectedDates, dateStr, instance) {
          if (dateStr === vm.selectedDate) return;
          vm.hasFetched = false;
          vm.slots = {};
          vm.appointmentSlot = null;
          vm.$emit('ready', false);
          vm.$emit('update', null);
          vm.updateDate(dateStr);
        },
        onReady: function (selectedDates, dateStr, instance) {
          vm.initialized = true;
          vm.applyInitialCalendarDate(instance);

          setTimeout(() => {
            const visibleDates = vm.getFirstAndLastVisibleDates(instance);
            if (visibleDates && !vm.initialAvailableDates) {
              vm.getAvailableDates(visibleDates);
            } else if (visibleDates) {
              vm.refreshCalendarDates();
            }
          }, 100);
        },
        onMonthChange: function (selectedDates, dateStr, instance) {
          vm.targetMonth = { month: instance.currentMonth, year: instance.currentYear };
          setTimeout(() => {
            const visibleDates = vm.getFirstAndLastVisibleDates(instance);
            if (visibleDates) {
              vm.getAvailableDates(visibleDates);
            }
          }, 50);
        },
      });

      if (!vm.initialCalendarDateApplied) {
        vm.updateDate(vm.startingDate());
      }
    },

    applyInitialCalendarDate(instance) {
      const initialDate = this.startingDate();
      if (!initialDate || !instance || this.initialCalendarDateApplied) return;

      this.initialCalendarDateApplied = true;
      this.updateDate(initialDate);
      instance.setDate(initialDate, false);
      instance.jumpToDate(initialDate);
    },

    refreshCalendarDates() {
      if (!this.calendar) return;

      const vm = this;

      this.calendar.set('disable', [
        function (date) {
          const days = !vm.settings['days_of_week'].includes(date.getDay().toString());
          let startingBefore = false;
          if (vm.startingDate() !== null) {
            const startDate = moment(vm.startingDate());
            if (moment(date).isBefore(startDate, 'day')) {
              startingBefore = true;
            }
          }
          return days || startingBefore;
        },
      ]);

      this.calendar.redraw();

      const activeMonth = this.targetMonth ? this.targetMonth.month : this.calendar.currentMonth;
      const activeYear = this.targetMonth ? this.targetMonth.year : this.calendar.currentYear;

      const currentMonthAvailableDates = Object.keys(this.availableDates)
        .filter((dateString) => {
          if (!this.availableDates[dateString]) return false;
          const [y, m] = dateString.split('-').map(Number);
          return m - 1 === activeMonth && y === activeYear;
        })
        .sort();

      const currentSelectedDate = this.calendar.selectedDates[0];

      if (!currentSelectedDate) {
        if (currentMonthAvailableDates.length > 0) {
          const dateToSelect = currentMonthAvailableDates[0];
          this.selectedDate = dateToSelect;
          this.calendar.selectedDates = [moment(dateToSelect).toDate()];
          this.calendar.jumpToDate(dateToSelect);
          this.calendar.redraw();
        }
      } else {
        const selectedIsInActiveMonth =
          moment(currentSelectedDate).month() === activeMonth &&
          moment(currentSelectedDate).year() === activeYear;

        if (
          !selectedIsInActiveMonth ||
          (currentMonthAvailableDates.length > 0 &&
            moment(currentSelectedDate).isBefore(currentMonthAvailableDates[0], 'day')) // 👈 fix here
        ) {
          if (currentMonthAvailableDates.length > 0) {
            const firstAvailableDate = currentMonthAvailableDates[0];
            vm.selectedDate = firstAvailableDate;
            vm.calendar.selectedDates = [moment(firstAvailableDate).toDate()];
            vm.calendar.jumpToDate(firstAvailableDate);
            vm.calendar.redraw();
            vm.appointmentSlot = null;
            vm.$emit('ready', false);
            vm.$emit('update', null);
          }
        }
      }

      this.targetMonth = null;
    },

    getAvailableDates(dates) {
      const firstDate = this.formatLocal(new Date(dates.firstDate));
      const lastDate = this.formatLocal(new Date(dates.lastDate));
      const cacheKey = `${firstDate}_${lastDate}`;

      if (this.availableDatesCache[cacheKey]) {
        this.availableDates = this.availableDatesCache[cacheKey];
        this.advanceToNextAvailableIfNeeded();
        this.refreshCalendarDates();

        if (this.pendingSlotFetch) {
          this.pendingSlotFetch = false;
          this.getAvailability();
        }
        return;
      }

      this.availableSlotsLoading = true;

      axios
        .post(route('scheduler.available-dates', { key: this.kioskKey }), {
          startDate: dates.firstDate,
          endDate: dates.lastDate,
          services: this.services.map((service) => service.kiosk_job_id),
          tireConnectData: this.validTireConnectData,
        })
        .then((response) => {
          this.availableDates = response.data.data.dates;
          this.availableDatesCache[cacheKey] = response.data.data.dates;
          this.advanceToNextAvailableIfNeeded();
        })
        .finally(() => {
          this.availableSlotsLoading = false;
          this.refreshCalendarDates();

          if (this.pendingSlotFetch) {
            this.pendingSlotFetch = false;
            this.getAvailability();
          }
        });
    },

    advanceToNextAvailableIfNeeded() {
      if (!this.selectedDate || this.availableDates[this.selectedDate]) return;

      const nextAvailable = Object.keys(this.availableDates)
        .filter((d) => this.availableDates[d] > 0 && d > this.selectedDate)
        .sort()[0];

      if (nextAvailable) {
        this.updateDate(nextAvailable);
        if (this.calendar) {
          this.calendar.setDate(nextAvailable, false);
        }
      }
    },

    getFirstAndLastVisibleDates(flatpickrInstance) {
      if (!flatpickrInstance) return null;

      const calendarContainer = flatpickrInstance.calendarContainer;
      const dayElements = calendarContainer.querySelectorAll('.flatpickr-day');

      if (dayElements.length === 0) return null;

      const firstDate = this.getDateFromDayElement(dayElements[0], flatpickrInstance);
      const lastDate = this.getDateFromDayElement(
        dayElements[dayElements.length - 1],
        flatpickrInstance,
      );

      this.startDate = firstDate;
      this.lastDate = lastDate;

      return {
        firstDate,
        lastDate,
        totalVisibleDays: dayElements.length,
      };
    },

    getDateFromDayElement(dayElement, flatpickrInstance) {
      const dayNum = parseInt(dayElement.textContent);
      let month = flatpickrInstance.currentMonth;
      let year = flatpickrInstance.currentYear;

      if (dayElement.classList.contains('prevMonthDay')) {
        month = month === 0 ? 11 : month - 1;
        if (month === 11) year--;
      } else if (dayElement.classList.contains('nextMonthDay')) {
        month = month === 11 ? 0 : month + 1;
        if (month === 0) year++;
      }

      return new Date(year, month, dayNum);
    },

    updateDate(date) {
      this.hasFetched = false;
      this.slots = {};
      this.selectedDate = date;
    },

    formatLocal(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },

    getAvailability() {
      if (this.availableSlotsLoading) {
        this.pendingSlotFetch = true;
        return;
      }

      // Cancel any in-flight request
      if (this.availabilityAbortController) {
        this.availabilityAbortController.abort();
      }

      const controller = new AbortController();
      this.availabilityAbortController = controller;

      this.loading = true;
      this.hasFetched = false;

      axios
        .post(
          route('scheduler.availability', { key: this.kioskKey }),
          {
            date: this.selectedDate,
            services: this.services.map((service) => service.kiosk_job_id),
            tireConnectData: this.validTireConnectData,
          },
          { signal: controller.signal },
        )
        .then((response) => {
          this.slots = response.data.data.slots ?? {};
          if (!this.hasSlots) {
            const existing = this.calendar.config.disable || [];
            const existingFns = existing.filter((i) => typeof i === 'function');
            this.calendar.set('disable', [
              ...existingFns,
              (date) => this.formatLocal(date) === this.selectedDate,
            ]);
          }
        })
        .catch((error) => {
          if (axios.isCancel(error) || error.name === 'CanceledError') return;
          console.error('getAvailability error:', error);
          genericError();
        })
        .finally(() => {
          // Only update state if this is still the active request
          if (controller.signal.aborted) return;
          this.loading = false;
          this.hasFetched = true;
        });
    },

    formatTo12Hour(isoString) {
      const date = moment.parseZone(isoString);
      return {
        time: date.format('h:mm'),
        period: date.format('A'),
      };
    },
  },
};
</script>

<template>
  <Card class="mt-3" title="Select an Available Date and Time">
    <div class="row">
      <div class="col-12 col-md-6 mb-5">
        <div class="calendar-wrapper">
          <div id="scheduler_calendar" />
          <div v-if="availableSlotsLoading" class="calendar-loading-overlay">
            <Loading />
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6">
        <Loading v-if="loading" />

        <template v-if="!loading && hasFetched && hasSlots">
          <div class="list-group" style="max-height: 400px; overflow-y: auto">
            <button
              v-for="(availability, slot) in slots"
              :key="slot"
              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center mb-3 secondary-color"
              :class="{ selected: slot === appointmentSlot }"
              @click="updateSlot(slot, availability)"
            >
              <span class="availability-text">{{ formatTo12Hour(slot).time }}</span>
              <span class="text-note availability-text">{{ availability }}</span>
              <span class="text-muted availability-text">{{ formatTo12Hour(slot).period }}</span>
            </button>
          </div>
        </template>

        <template
          v-else-if="!loading && !availableSlotsLoading && hasFetched && !hasSlots && selectedDate"
        >
          <div class="mt-2">
            <Alert icon="fas fa-calendar-times secondary-color">
              <div class="text-center">
                <strong>No appointments available</strong><br />
                Please check back later or try a different day.
              </div>
            </Alert>
          </div>
        </template>
      </div>
    </div>
  </Card>
</template>

<style scoped>
.calendar-wrapper {
  position: relative;
}

.calendar-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 0.25rem;
  z-index: 10;
}

.availability-text {
  color: var(--ink-primary-color) !important;
}

.text-note {
  font-size: 0.7rem;
  font-style: italic;
  text-align: end;
}

.selected {
  border: 2px solid #007bff;
  border-radius: 0.25rem;
  box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
}

:deep(.flatpickr-day.no-slots) {
  opacity: 0.35;
  color: #aaa;
  text-decoration: line-through;
  pointer-events: none;
}
</style>
